import { format } from 'date-fns';
import { ClassifierData } from 'types/classifiers';
import {
    ExperienceRecordCreateDto,
    ExperienceRecordDetailsDto,
    ExperienceRecordFormData,
} from 'types/experience-records.ts';
import { getOptionsFromClassifier } from 'utils/formUtil.ts';
import { DEFAULT_DATE_FORMAT } from 'utils/parseDate.ts';
import { sortByField } from 'utils/sort.ts';

export const EXPERIENCE_RECORD_FORM_CLASSIFIERS = ['CURRENCY', 'COUNTRY', 'BUSINESS_AREA_TYPE'];

export const defaultXRFormValues: ExperienceRecordFormData = {
    clientParty: null,
    clientName: '',
    isConfidential: true,
    date: format(new Date(), DEFAULT_DATE_FORMAT),
    clientConsentRequestedOn: null,
    clientConsentReceivedOn: null,
    countries: [],
    name: '',
    status: 'FINAL',
    description: '',
    descriptionLocalLang: '',
    valueAmount: null,
    currency: null,
    isValueConfidential: false,
    isShowcasePriority: false,
    isUsedInMarketing: false,
    isIncludedInLegalDirectories: false,
    matters: [],
    parties: [],
    // Readonly fields - populated from client and connected cases
    connectedBusinessAreas: '',
    connectedSectors: '',
    supervisedBy: '',
    responsibleLawyers: '',
    handledByEmployees: '',
};

export const experienceRecordDtoToForm = (
    experienceRecord: ExperienceRecordDetailsDto,
    classifiers: Record<string, ClassifierData>
): ExperienceRecordFormData => {
    const countryClassifiers = getOptionsFromClassifier<string, string>(classifiers['COUNTRY']);
    return {
        name: experienceRecord.name ?? '',
        status: experienceRecord.status ?? 'FINAL',
        description: experienceRecord.description ?? '',
        descriptionLocalLang: experienceRecord.descriptionLocalLang ?? '',
        date: experienceRecord.date ?? '', // ISO date string
        isConfidential: experienceRecord.isConfidential ?? true,
        clientName: experienceRecord.clientName ?? '',
        clientParty: experienceRecord.clientParty ?? null,
        valueAmount: experienceRecord.valueAmount ?? null,
        currency:
            getOptionsFromClassifier<string, string>(classifiers['CURRENCY']).find(
                (val) => val.value === experienceRecord.currencyTypeCode
            ) ?? null,
        isValueConfidential: experienceRecord.isValueConfidential ?? false,
        isShowcasePriority: experienceRecord.isShowcasePriority ?? false,
        isUsedInMarketing: experienceRecord.isUsedInMarketing ?? false,
        isIncludedInLegalDirectories: experienceRecord.isIncludedInLegalDirectories ?? false,
        clientConsentRequestedOn: experienceRecord.clientConsentRequestedOn ?? null,
        clientConsentReceivedOn: experienceRecord.clientConsentReceivedOn ?? null,
        countries: (experienceRecord.countryTypeCodes || [])
            .map((code) => countryClassifiers.find((opt) => opt.value === code))
            .filter((it) => it !== undefined),
        matters: experienceRecord.matters,
        parties: sortByField(experienceRecord.parties, 'name'),

        connectedBusinessAreas: null,
        connectedSectors: null,
        supervisedBy: null,
        responsibleLawyers: null,
        handledByEmployees: null,
    };
};

/** The update replaces the record: null matters, countries or parties clear them, so the lists always go in full. */
export const experienceRecordFormToRequest = (form: ExperienceRecordFormData): ExperienceRecordCreateDto => ({
    clientPartyId: form.clientParty!.id,
    clientName: form.clientName,
    isConfidential: form.isConfidential,
    date: form.date,
    clientConsentRequestedOn: form.clientConsentRequestedOn!,
    clientConsentReceivedOn: form.clientConsentReceivedOn!,
    countryTypeCodes: form.countries?.map((c) => c.value),
    name: form.name!,
    status: form.status,
    description: form.description ?? undefined,
    descriptionLocalLang: form.descriptionLocalLang ?? undefined,
    valueAmount: form.valueAmount ?? undefined,
    currencyTypeCode: form.currency?.value,
    isValueConfidential: form.isValueConfidential,
    isShowcasePriority: form.isShowcasePriority,
    isUsedInMarketing: form.isUsedInMarketing,
    isIncludedInLegalDirectories: form.isIncludedInLegalDirectories,
    matterIds: form.matters?.map((m) => m.id),
    partyIds: form.parties?.map((m) => m.id),
});

/**
 * The form fields each card of the record page edits. The name is generated from the client and the date, so Core
 * details saves it; the read-only fields the form derives from the client and the cases are never sent.
 */
export const EXPERIENCE_RECORD_SECTION_FIELDS = {
    'core-details': [
        'status',
        'clientParty',
        'clientName',
        'isConfidential',
        'countries',
        'date',
        'clientConsentRequestedOn',
        'clientConsentReceivedOn',
        'name',
    ],
    description: ['description', 'descriptionLocalLang'],
    value: ['valueAmount', 'currency', 'isValueConfidential'],
    'connected-cases': ['matters'],
    marketing: ['isShowcasePriority', 'isUsedInMarketing', 'isIncludedInLegalDirectories', 'parties'],
} satisfies Record<string, (keyof ExperienceRecordFormData)[]>;

export type ExperienceRecordSectionId = keyof typeof EXPERIENCE_RECORD_SECTION_FIELDS;
